"use client";

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSearchParams } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { MovingBorder } from "@/components/aceternity/moving-border";
import { MultiStepLoader } from "@/components/aceternity/multi-step-loader";
import { Meteors } from "@/components/aceternity/meteors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  CheckCircle,
  Shield,
  Clock,
  Sparkles,
  Target,
  CreditCard,
} from "lucide-react";

const demoSchema = z.object({
  first_name: z.string().min(2, "First name is required").max(50),
  last_name: z.string().min(2, "Last name is required").max(50),
  email: z.string().email("Valid work email required"),
  phone: z.string().optional(),
  company_name: z.string().min(2, "Company name is required").max(100),
  company_website: z.string().url("Must be a valid URL").optional().or(z.literal("")),
  company_size: z.enum(["1-10", "11-50", "51-200", "201-500", "500+"], {
    required_error: "Select company size",
  }),
  job_title: z.string().min(2, "Job title is required").max(100),
  current_tools: z.array(z.string()).optional(),
  monthly_outbound_volume: z
    .enum(["<500", "500-2,000", "2,000-10,000", "10,000+"])
    .optional(),
  biggest_challenge: z.string().max(500).optional(),
  heard_about_us: z.string().optional(),
});

type DemoFormData = z.infer<typeof demoSchema>;

const tools = [
  "Apollo",
  "Outreach",
  "Instantly",
  "Lemlist",
  "Clay",
  "11x",
  "Artisan",
  "HubSpot",
  "Salesforce",
  "Other",
];

const loadingStates = [
  { text: "Saving your information..." },
  { text: "Notifying our team..." },
  { text: "Preparing your personalized demo..." },
];

const benefits = [
  { icon: Clock, text: "30-minute personalized walkthrough" },
  { icon: Sparkles, text: "See AI agents draft messages in real-time" },
  { icon: Shield, text: "Learn how approval workflows protect your brand" },
  { icon: Target, text: "Get a custom outbound strategy for your ICP" },
  { icon: CreditCard, text: "No commitment. No credit card." },
];

export function DemoBookingForm() {
  const searchParams = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const [loaderStep, setLoaderStep] = useState(0);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showExtra, setShowExtra] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<DemoFormData>({
    resolver: zodResolver(demoSchema),
    mode: "onBlur",
    defaultValues: { current_tools: [] },
  });

  const selectedTools = watch("current_tools") ?? [];
  const requiredFieldsFilled = watch("first_name") && watch("last_name") && watch("email") && watch("company_name") && watch("job_title") && watch("company_size");

  useEffect(() => {
    if (requiredFieldsFilled) setShowExtra(true);
  }, [requiredFieldsFilled]);

  const toggleTool = (tool: string) => {
    const current = selectedTools;
    const updated = current.includes(tool)
      ? current.filter((t) => t !== tool)
      : [...current, tool];
    setValue("current_tools", updated);
  };

  const onSubmit = async (data: DemoFormData) => {
    setSubmitting(true);
    setLoaderStep(0);
    setError(null);

    const payload = {
      ...data,
      utm_source: searchParams.get("utm_source") ?? "",
      utm_medium: searchParams.get("utm_medium") ?? "",
      utm_campaign: searchParams.get("utm_campaign") ?? "",
      utm_content: searchParams.get("utm_content") ?? "",
      utm_term: searchParams.get("utm_term") ?? "",
      referrer: typeof document !== "undefined" ? document.referrer : "",
      landing_page: typeof window !== "undefined" ? window.location.pathname : "",
      honeypot: "",
    };

    // Animate through loader steps
    const stepInterval = setInterval(() => {
      setLoaderStep((prev) => Math.min(prev + 1, loadingStates.length - 1));
    }, 800);

    try {
      const response = await fetch("/api/marketing/demo-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      clearInterval(stepInterval);
      setLoaderStep(loadingStates.length);

      if (result.success) {
        setTimeout(() => setSuccess(true), 500);
      } else {
        setError(result.error?.message ?? "Something went wrong. Please try again.");
        setSubmitting(false);
      }
    } catch {
      clearInterval(stepInterval);
      setError("Network error. Please check your connection and try again.");
      setSubmitting(false);
    }
  };

  return (
    <section className="relative min-h-screen overflow-hidden px-4 py-16 sm:px-6">
      {/* Background */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <Meteors number={15} />
        <div className="absolute left-1/4 top-1/4 h-96 w-96 rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 h-96 w-96 rounded-full bg-purple-500/5 blur-[120px]" />
      </div>

      <div className="relative z-10 mx-auto max-w-6xl">
        <div className="mb-12 text-center">
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
            See Peakflow in Action
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-lg text-muted-foreground">
            Book a personalized demo and discover how supervised AI outbound can transform your pipeline.
          </p>
        </div>

        <AnimatePresence mode="wait">
          {success ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mx-auto max-w-lg text-center"
            >
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-green-500/10">
                <CheckCircle className="h-10 w-10 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-foreground">You&apos;re All Set!</h2>
              <p className="mt-3 text-muted-foreground">
                Our team will reach out within 24 hours to schedule your personalized demo.
                Check your inbox for a confirmation email.
              </p>
              <Button className="mt-8" asChild>
                <a href="/">Back to Homepage</a>
              </Button>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="grid gap-10 lg:grid-cols-5"
            >
              {/* Form */}
              <div className="lg:col-span-3">
                <MovingBorder
                  duration={4000}
                  containerClassName="w-full"
                  className="p-6 sm:p-8"
                >
                  {submitting ? (
                    <div className="flex min-h-[300px] items-center justify-center">
                      <MultiStepLoader
                        loadingStates={loadingStates}
                        loading={submitting}
                        currentState={loaderStep}
                      />
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                      {error && (
                        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                          {error}
                        </div>
                      )}

                      {/* Name Row */}
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <Label htmlFor="first_name">First Name *</Label>
                          <Input
                            id="first_name"
                            {...register("first_name")}
                            placeholder="Jane"
                            className="mt-1.5"
                          />
                          {errors.first_name && (
                            <p className="mt-1 text-xs text-destructive">{errors.first_name.message}</p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="last_name">Last Name *</Label>
                          <Input
                            id="last_name"
                            {...register("last_name")}
                            placeholder="Smith"
                            className="mt-1.5"
                          />
                          {errors.last_name && (
                            <p className="mt-1 text-xs text-destructive">{errors.last_name.message}</p>
                          )}
                        </div>
                      </div>

                      {/* Email & Phone */}
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <Label htmlFor="email">Work Email *</Label>
                          <Input
                            id="email"
                            type="email"
                            {...register("email")}
                            placeholder="jane@company.com"
                            className="mt-1.5"
                          />
                          {errors.email && (
                            <p className="mt-1 text-xs text-destructive">{errors.email.message}</p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="phone">Phone (optional)</Label>
                          <Input
                            id="phone"
                            type="tel"
                            {...register("phone")}
                            placeholder="+1 (555) 000-0000"
                            className="mt-1.5"
                          />
                        </div>
                      </div>

                      {/* Company */}
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <Label htmlFor="company_name">Company Name *</Label>
                          <Input
                            id="company_name"
                            {...register("company_name")}
                            placeholder="Acme Inc"
                            className="mt-1.5"
                          />
                          {errors.company_name && (
                            <p className="mt-1 text-xs text-destructive">{errors.company_name.message}</p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="company_website">Company Website</Label>
                          <Input
                            id="company_website"
                            {...register("company_website")}
                            placeholder="https://acme.com"
                            className="mt-1.5"
                          />
                          {errors.company_website && (
                            <p className="mt-1 text-xs text-destructive">{errors.company_website.message}</p>
                          )}
                        </div>
                      </div>

                      {/* Job Title & Company Size */}
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <Label htmlFor="job_title">Job Title *</Label>
                          <Input
                            id="job_title"
                            {...register("job_title")}
                            placeholder="VP of Sales"
                            className="mt-1.5"
                          />
                          {errors.job_title && (
                            <p className="mt-1 text-xs text-destructive">{errors.job_title.message}</p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="company_size">Company Size *</Label>
                          <select
                            id="company_size"
                            {...register("company_size")}
                            className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            <option value="">Select...</option>
                            <option value="1-10">1-10</option>
                            <option value="11-50">11-50</option>
                            <option value="51-200">51-200</option>
                            <option value="201-500">201-500</option>
                            <option value="500+">500+</option>
                          </select>
                          {errors.company_size && (
                            <p className="mt-1 text-xs text-destructive">{errors.company_size.message}</p>
                          )}
                        </div>
                      </div>

                      {/* Progressive Disclosure - Extra Fields */}
                      <AnimatePresence>
                        {showExtra && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="space-y-6 overflow-hidden"
                          >
                            {/* Current Tools */}
                            <div>
                              <Label>What tools do you currently use?</Label>
                              <div className="mt-2 flex flex-wrap gap-2">
                                {tools.map((tool) => (
                                  <button
                                    key={tool}
                                    type="button"
                                    onClick={() => toggleTool(tool)}
                                    className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
                                      selectedTools.includes(tool)
                                        ? "border-primary bg-primary/10 text-primary"
                                        : "border-border text-muted-foreground hover:border-primary/50"
                                    }`}
                                  >
                                    {tool}
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Volume */}
                            <div>
                              <Label htmlFor="monthly_outbound_volume">Monthly Outbound Volume</Label>
                              <select
                                id="monthly_outbound_volume"
                                {...register("monthly_outbound_volume")}
                                className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              >
                                <option value="">Select...</option>
                                <option value="<500">Less than 500</option>
                                <option value="500-2,000">500 - 2,000</option>
                                <option value="2,000-10,000">2,000 - 10,000</option>
                                <option value="10,000+">10,000+</option>
                              </select>
                            </div>

                            {/* Challenge */}
                            <div>
                              <Label htmlFor="biggest_challenge">Biggest Outbound Challenge</Label>
                              <textarea
                                id="biggest_challenge"
                                {...register("biggest_challenge")}
                                placeholder="What's your biggest outbound challenge right now?"
                                rows={3}
                                className="mt-1.5 flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              />
                            </div>

                            {/* How did you hear */}
                            <div>
                              <Label htmlFor="heard_about_us">How did you hear about us?</Label>
                              <select
                                id="heard_about_us"
                                {...register("heard_about_us")}
                                className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              >
                                <option value="">Select...</option>
                                <option value="google">Google Search</option>
                                <option value="linkedin">LinkedIn</option>
                                <option value="twitter">Twitter / X</option>
                                <option value="referral">Referral</option>
                                <option value="blog">Blog / Article</option>
                                <option value="podcast">Podcast</option>
                                <option value="other">Other</option>
                              </select>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* Honeypot */}
                      <input
                        type="text"
                        name="honeypot"
                        tabIndex={-1}
                        autoComplete="off"
                        className="absolute -left-[9999px] h-0 w-0 opacity-0"
                      />

                      <Button type="submit" size="lg" className="w-full text-base">
                        Book My Demo
                      </Button>
                    </form>
                  )}
                </MovingBorder>
              </div>

              {/* Value Reinforcement */}
              <div className="lg:col-span-2">
                <div className="sticky top-24 space-y-8">
                  <div>
                    <h3 className="mb-6 text-lg font-semibold text-foreground">
                      What to Expect From Your Demo
                    </h3>
                    <ul className="space-y-4">
                      {benefits.map((benefit) => (
                        <li key={benefit.text} className="flex items-start gap-3">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                            <benefit.icon className="h-4 w-4 text-primary" />
                          </div>
                          <span className="pt-1 text-sm text-muted-foreground">{benefit.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Testimonial */}
                  <div className="rounded-xl border border-border bg-card/50 p-5">
                    <p className="text-sm italic text-muted-foreground">
                      &ldquo;The demo blew me away. Within 20 minutes I understood exactly how Peakflow
                      could replace our entire outbound stack. We signed up the same day.&rdquo;
                    </p>
                    <div className="mt-4 flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-primary/20" />
                      <div>
                        <div className="text-sm font-medium text-foreground">Priya Sharma</div>
                        <div className="text-xs text-muted-foreground">CEO, CloudPeak SaaS</div>
                      </div>
                    </div>
                  </div>

                  {/* Trust */}
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Shield className="h-3.5 w-3.5" />
                      SOC 2
                    </div>
                    <div className="flex items-center gap-1">
                      <Shield className="h-3.5 w-3.5" />
                      GDPR
                    </div>
                    <span>Used by 500+ teams</span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
